import React, { useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

interface MultiSelectDropdownProps {
  icon: React.ElementType;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  emptyText: string;
  isLoading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, value: string) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>, value: string) => void;
  variant?: "icon" | "full";
  showSelectAll?: boolean;
  singleSelect?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  icon: Icon,
  selectedValues,
  onChange,
  options,
  placeholder,
  emptyText,
  isLoading = false,
  searchable = false,
  searchPlaceholder = "Suchen...",
  draggable = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  variant = "full",
  showSelectAll = true,
  singleSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const draggedValueRef = useRef<string | null>(null);

  const toggleOption = (value: string) => {
    if (singleSelect) {
      onChange([value]);
      setIsOpen(false);
    } else {
      const newValues = selectedValues.includes(value) ? selectedValues.filter((v) => v !== value) : [...selectedValues, value];
      onChange(newValues);
    }
  };

  const toggleSelectAll = () => {
    if (singleSelect) return;

    const allFilteredValues = filteredOptions.map((option) => option.value);
    const areAllSelected = allFilteredValues.every((value) => selectedValues.includes(value));

    if (areAllSelected) {
      // Remove all filtered values from selection
      onChange(selectedValues.filter((value) => !allFilteredValues.includes(value)));
    } else {
      // Add all filtered values to selection
      onChange(Array.from(new Set([...selectedValues, ...allFilteredValues])));
    }
  };

  const displayText =
    selectedValues.length > 0
      ? singleSelect
        ? options.find((opt) => opt.value === selectedValues[0])?.label || emptyText
        : `${placeholder}: ${selectedValues.length} Gew.`
      : emptyText;

  // Filter options based on search term
  const filteredOptions = searchable ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;

  // Check if all filtered options are selected
  const areAllFilteredSelected = filteredOptions.length > 0 && filteredOptions.every((option) => selectedValues.includes(option.value));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest(`.dropdown-container-${placeholder.replace(/\s+/g, "-")}`)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, placeholder]);

  const getDropdownPosition = () => {
    if (!containerRef.current) return {};

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 256; // w-64 = 16rem = 256px

    // Check if dropdown would extend beyond viewport right
    const wouldExtendBeyondRight = rect.left + dropdownWidth > viewportWidth;

    if (wouldExtendBeyondRight) {
      return {
        right: "0",
        left: "auto",
      };
    }

    return {
      left: "0",
      right: "auto",
    };
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, value: string) => {
    if (draggable && onDragStart) {
      e.stopPropagation();
      onDragStart(e, value);
      e.currentTarget.classList.add("opacity-50");
      draggedValueRef.current = value;
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (draggable && onDragEnd) {
      e.stopPropagation();
      onDragEnd(e);
      e.currentTarget.classList.remove("opacity-50");
      draggedValueRef.current = null;
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (draggable && onDragOver) {
      e.preventDefault();
      e.stopPropagation();
      onDragOver(e);

      const targetValue = e.currentTarget.getAttribute("data-value");

      // Wenn es das gleiche Element ist oder kein Wert vorhanden, zeige keinen Indikator
      if (!draggedValueRef.current || !targetValue || draggedValueRef.current === targetValue) {
        const container = e.currentTarget.closest(".dropdown-content");
        container?.querySelectorAll(".drop-indicator").forEach((el) => el.remove());
        return;
      }

      // Finde die Positionen der Elemente in der Liste
      const container = e.currentTarget.closest(".dropdown-content");
      if (!container) return;

      const items = Array.from(container.children);
      const draggedIndex = items.findIndex((item) => item.getAttribute("data-value") === draggedValueRef.current);
      const targetIndex = items.findIndex((item) => item === e.currentTarget);

      // Wenn das gezogene Element vor dem Ziel ist, zeige den Indikator nur am unteren Rand
      // Wenn das gezogene Element nach dem Ziel ist, zeige den Indikator nur am oberen Rand
      const shouldShowTop = draggedIndex > targetIndex;
      const shouldShowBottom = draggedIndex < targetIndex;

      // Entferne alle existierenden Indikatoren
      container.querySelectorAll(".drop-indicator").forEach((el) => el.remove());

      // Erstelle einen neuen Indikator
      const indicator = document.createElement("div");
      indicator.className = "drop-indicator absolute left-0 right-0 h-0.5 bg-primary";

      if (shouldShowTop) {
        indicator.style.top = "0";
        e.currentTarget.insertBefore(indicator, e.currentTarget.firstChild);
      } else if (shouldShowBottom) {
        indicator.style.bottom = "0";
        e.currentTarget.appendChild(indicator);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (draggable && onDragLeave) {
      e.stopPropagation();
      onDragLeave(e);
      const container = e.currentTarget.closest(".dropdown-content");
      container?.querySelectorAll(".drop-indicator").forEach((el) => el.remove());
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, value: string) => {
    if (draggable && onDrop) {
      e.preventDefault();
      e.stopPropagation();
      onDrop(e, value);
      const container = e.currentTarget.closest(".dropdown-content");
      container?.querySelectorAll(".drop-indicator").forEach((el) => el.remove());
      draggedValueRef.current = null;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${variant === "icon" ? "w-auto" : "w-48"} dropdown-container-${placeholder.replace(/\s+/g, "-")}`}>
      {variant === "icon" ? (
        <button className={`btn btn-sm ${isOpen ? "bg-[#222222]" : "bg-[#1f1f1f]"} border-none hover:bg-[#222222]`} onClick={() => setIsOpen(!isOpen)}>
          <Icon className="w-5 h-5" />
        </button>
      ) : (
        <div
          className={`h-10 w-48 rounded-full flex items-center px-3 cursor-pointer border border-[#292929] ${selectedValues.length > 0 ? "bg-primary bg-opacity-50" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon className={`w-4 h-4 mr-2 ${selectedValues.length > 0 ? "text-white" : "text-gray-400"}`} />
          <span className="flex-grow truncate">{displayText}</span>
          {selectedValues.length > 0 ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              className="ml-2 p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
              title="Filter zurücksetzen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          ) : (
            <ChevronDownIcon className={`w-4 h-4 ml-2 ${isOpen ? "transform rotate-180" : ""} transition-transform duration-200`} />
          )}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-[#1f1f1f] border border-[#292929] rounded-md shadow-lg" style={getDropdownPosition()}>
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-400">Wird geladen...</div>
          ) : options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">Keine Optionen verfügbar</div>
          ) : (
            <>
              {searchable && (
                <div className="p-2 border-b border-[#292929]">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={searchPlaceholder}
                      className="input input-sm input-bordered w-full pl-9"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                </div>
              )}

              {!singleSelect && showSelectAll && filteredOptions.length > 0 && (
                <div
                  className="flex items-center px-4 py-2 hover:bg-[#222222] cursor-pointer border-b border-[#292929]"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelectAll();
                  }}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm mr-3"
                    checked={areAllFilteredSelected}
                    onChange={() => {}} // Handled by parent div click
                  />
                  <span className="font-medium">Alle auswählen</span>
                </div>
              )}

              <div className="max-h-60 overflow-auto dropdown-content">
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center px-4 py-2 hover:bg-[#222222] cursor-pointer relative"
                    data-value={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(option.value);
                    }}
                    draggable={draggable}
                    onDragStart={(e) => handleDragStart(e, option.value)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, option.value)}
                  >
                    {!singleSelect && (
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm mr-3"
                        checked={selectedValues.includes(option.value)}
                        onChange={() => {}} // Handled by parent div click
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
